/**
 * Pairing card charges with their receipts.
 *
 * The amount has to agree - that is what makes a match a match - and the date and the merchant
 * name only decide how confident we are. Everything below `MIN_SCORE` is not offered at all,
 * because a wrong automatic match is worse than no suggestion.
 */

export type MatchableTransaction = {
  id: string;
  bookingDate: string;
  amount: number;
  counterparty?: string;
  description?: string;
};

export type MatchableDocument = {
  id: string;
  kind: "ER" | "AR" | "KA";
  grossTotal?: number;
  /** Tip on top of a receipt: the card is charged receipt plus tip. */
  tip?: number;
  issueDate?: string;
  paidDate?: string;
  partnerName?: string;
  docNumber?: string;
};

export type MatchReason = "amount" | "date" | "name";

export type Match = {
  documentId: string;
  score: number;
  reasons: MatchReason[];
};

/** Below this a suggestion is not worth showing. */
export const MIN_SCORE = 0.7;
/** Amounts that differ by more than this are a different payment. */
const AMOUNT_TOLERANCE = 0.02;

const STOP_WORDS = new Set([
  "gmbh",
  "ag",
  "og",
  "kg",
  "eu",
  "eu.",
  "e.u.",
  "co",
  "ltd",
  "inc",
  "der",
  "die",
  "das",
  "und",
  "the",
  "at",
  "wien",
  "graz",
  "salzburg",
  "linz",
  "gmbh.",
]);

/** Statements transliterate umlauts ("BUEROBEDARF"), so both sides are folded the same way. */
function fold(value: string): string {
  return value
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss");
}

function tokens(value: string | undefined): string[] {
  return fold(value ?? "")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .split(" ")
    .filter((token) => token.length >= 3 && !STOP_WORDS.has(token));
}

/** Share of the document's name tokens that also appear in the statement line. */
export function nameSimilarity(
  counterparty: string | undefined,
  partner: string | undefined,
): number {
  const left = new Set(tokens(counterparty));
  const right = tokens(partner);
  if (left.size === 0 || right.length === 0) return 0;
  const hits = right.filter((token) => left.has(token)).length;
  return hits / right.length;
}

function daysBetween(left: string, right: string): number {
  const a = Date.parse(`${left.slice(0, 10)}T00:00:00Z`);
  const b = Date.parse(`${right.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return Number.POSITIVE_INFINITY;
  return Math.abs(a - b) / 86_400_000;
}

/**
 * 0 when the amounts do not agree, otherwise 0.6 for the amount plus up to 0.3 for how close the
 * dates are and up to 0.1 for the merchant name.
 */
export function scoreMatch(
  transaction: MatchableTransaction,
  document: MatchableDocument,
): Match | null {
  const gross = document.grossTotal ?? 0;
  if (gross <= 0) return null;
  // Money out pairs with a purchase, money in with an invoice we issued.
  const outgoing = transaction.amount < 0;
  if (outgoing !== (document.kind !== "AR")) return null;
  // With a tip the card shows receipt plus tip; a tip given in cash leaves just the receipt.
  const tip = document.kind === "AR" ? 0 : (document.tip ?? 0);
  const charged = Math.abs(transaction.amount);
  const fits = (amount: number) => Math.abs(charged - amount) <= AMOUNT_TOLERANCE;
  if (!fits(gross + tip) && !fits(gross)) return null;

  const reasons: MatchReason[] = ["amount"];
  let score = 0.6;

  const documentDate = document.paidDate || document.issueDate;
  if (documentDate) {
    const distance = daysBetween(transaction.bookingDate, documentDate);
    if (distance <= 3) {
      score += 0.3;
      reasons.push("date");
    } else if (distance <= 14) {
      score += 0.2;
      reasons.push("date");
    } else if (distance <= 45) {
      score += 0.1;
    }
  }

  const similarity = nameSimilarity(
    `${transaction.counterparty ?? ""} ${transaction.description ?? ""}`,
    document.partnerName,
  );
  if (similarity > 0) {
    score += 0.1 * similarity;
    if (similarity >= 0.5) reasons.push("name");
  }

  return { documentId: document.id, score: Math.round(Math.min(score, 1) * 100) / 100, reasons };
}

/**
 * Best suggestions per transaction, strongest first. A document is only offered to one
 * transaction, so a repeated monthly charge cannot claim the same receipt twice.
 */
export function suggestMatches(
  transactions: MatchableTransaction[],
  documents: MatchableDocument[],
  limit = 3,
): Map<string, Match[]> {
  const suggestions = new Map<string, Match[]>();
  const claimed = new Set<string>();

  const scored = transactions
    .map((transaction) => ({
      transaction,
      matches: documents
        .map((document) => scoreMatch(transaction, document))
        .filter((match): match is Match => match !== null && match.score >= MIN_SCORE)
        .sort((a, b) => b.score - a.score),
    }))
    // Settle the confident transactions first so they get first pick of the documents.
    .sort((a, b) => (b.matches[0]?.score ?? 0) - (a.matches[0]?.score ?? 0));

  for (const { transaction, matches } of scored) {
    const available = matches.filter((match) => !claimed.has(match.documentId)).slice(0, limit);
    if (available.length === 0) continue;
    claimed.add(available[0]!.documentId);
    suggestions.set(transaction.id, available);
  }
  return suggestions;
}

/**
 * The least the matcher links without asking: the amount plus a date within three days, or plus
 * a date within two weeks and the merchant's name.
 */
export const AUTO_SCORE = 0.9;

/**
 * The pairs that are safe to link without asking: the charge has exactly one plausible receipt,
 * that receipt has exactly one plausible charge, and the pair scores at least `AUTO_SCORE`.
 * Anything less certain stays a suggestion - two identical 9,99 € subscriptions are never
 * decided by a guess.
 */
export function autoMatches(
  transactions: MatchableTransaction[],
  documents: MatchableDocument[],
): Array<{ transactionId: string; documentId: string }> {
  const plausible = transactions.map((transaction) => ({
    transaction,
    matches: documents
      .map((document) => scoreMatch(transaction, document))
      .filter((match): match is Match => match !== null && match.score >= MIN_SCORE),
  }));
  const chargesPerDocument = new Map<string, number>();
  for (const { matches } of plausible) {
    for (const match of matches) {
      chargesPerDocument.set(match.documentId, (chargesPerDocument.get(match.documentId) ?? 0) + 1);
    }
  }
  return plausible
    .filter(
      ({ matches }) =>
        matches.length === 1 &&
        matches[0]!.score >= AUTO_SCORE &&
        chargesPerDocument.get(matches[0]!.documentId) === 1,
    )
    .map(({ transaction, matches }) => ({
      transactionId: transaction.id,
      documentId: matches[0]!.documentId,
    }));
}
